import moment from "moment";
import { escape } from 'lodash';

import { FormGroup } from "@angular/forms";
import { TemplateRef } from "@angular/core";

import { IScoreboardDetailsConfig } from "@app/modules/admin/scoreboard/details/scoreboard-details.component";

import { IConfirmationModalConfig } from "./confirmation-modal.service";

import { ITag } from "@lib/tag.interface";
import { IUser } from "@lib/users.interface";
import { ISmartFlow } from "@lib/auto-flow-interface";
import { ICompliance } from "@lib/self-signup.interface";
import { IVerifyEmail } from "@lib/user-bulk-delete.interface";
import { ISendoutConfirmation } from "@lib/sendout-settings.interface";

import { FuseConfirmationConfig } from "@fuse/services/confirmation";
import { DATE_FORMAT_WITHOUT_SECONDS, toParagraphCase } from "@app/utility/app.util";

export const DEFAULT_CONFIG: IConfirmationModalConfig = {
  title: 'Confirm action',
  message: 'Are you sure you want to confirm this action?',
  icon: {
    show: true,
    name: 'heroicons_outline:exclamation',
    color: 'warn',
  },
  actions: {
    confirm: {
      show: true,
      label: 'Confirm',
      color: 'warn',
    },
    cancel: {
      show: true,
      label: 'Cancel',
    },
  },
  dismissible: false,
  primaryActionValue: false,
}

export const deleteDepartmentConfig = (data: number[], templateRef: TemplateRef<HTMLElement> | null, form: FormGroup): IConfirmationModalConfig => {
  let message: string = `
    <p>
      Deleting ${data.length > 1 ? 'multiple departments' : 'a department'} will
      permanently remove ${data.length > 1 ? 'them' : 'it'} from the system.
    </p>
  `;

  const config: IConfirmationModalConfig = {
    title: `Delete Department`,
    message: message,
    icon: {
      show: true,
      name: 'heroicons_outline:exclamation',
      color: 'warn',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Delete',
        color: 'warn',
      },
      cancel: {
        show: true,
        label: 'Cancel',
      },
    },
    dismissible: true,
  };

  if (templateRef) {
    config.template = { templateType: 'custom', templateRef, customTemplateForm: form };
  }

  return config;
}

export const deleteRoleConfig = (data: { roles: any[], membersCount: number, roleName?: string }, templateRef: TemplateRef<HTMLElement>, form: FormGroup): IConfirmationModalConfig => {
  const count: number = data.roles.length;
  const subject: string = count === 1
    ? (data.roleName ? `the role <b>${escape(data.roleName)}</b>` : '1 role')
    : `${count} roles`;
  let message: string = `
    <p>
      Are you sure you want to delete ${subject}?
    </p>
    ${data.membersCount ? `</br>` : ''}
    <p>
      ${data.membersCount ? `Please note that all members currently assigned to this role
      must be moved to another role before proceeding.` : ''}
      Deleting a role is a permanent action and cannot be
      undone.
    </p>
    </br>
  `;

  return {
    title: `Delete Role`,
    message: message,
    icon: {
      show: true,
      name: 'heroicons_outline:exclamation',
      color: 'warn',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Delete',
        color: 'warn',
      },
      cancel: {
        show: true,
        label: 'Cancel',
      },
    },
    dismissible: true,
    template: {
      templateType: 'custom',
      templateRef: templateRef,
      customTemplateForm: form
    },
  };
}

export const duplicateRolePermission = (templateRef: TemplateRef<HTMLElement>, form: FormGroup): IConfirmationModalConfig => {
  let message: string = `
    <p>
      You are about to duplicate permissions from other roles.
      This action will overwrite all existing permissions for the
      selected role.
    </p>
    </br>
  `;

  return {
    title: `Duplicate permissions`,
    message: message,
    icon: {
      show: true,
      name: 'heroicons_outline:exclamation',
      color: 'warn',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Duplicate',
        color: 'warn',
      },
      cancel: {
        show: true,
        label: 'Cancel',
      },
    },
    dismissible: true,
    template: {
      templateType: 'custom',
      templateRef: templateRef,
      customTemplateForm: form
    },
  };
}

export const sendoutConfirmationConfig = (data: ISendoutConfirmation): IConfirmationModalConfig => {
  data.pulsing = data.pulsing.split('_').join(' ');
  let message: string = `
    <div>The selected post has Pulsing enabled. Pulsing setting is set to sent
      ${data.pulsingValue} ${data.pulsingValue > 1 ? 'messages' : 'message'} ${data.pulsing}
    </div>
  `;
  if (data.startingDate) message += `
    <div>Estimated date for first email: ${moment(data.startingDate).format(DATE_FORMAT_WITHOUT_SECONDS)}</div>
  `;
  if (data.closingDate) message += `
    <div>Estimated date for last email: ${moment(data.closingDate).format(DATE_FORMAT_WITHOUT_SECONDS)}</div>
  `;

  return {
    title: `Pulsing enabled`,
    message: message,
    icon: {
      show: true,
      name: 'heroicons_outline:exclamation',
      color: 'info',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Send now',
        color: 'primary',
      },
      cancel: {
        show: true,
        label: 'Send with pulsing',
      },
    },
    dismissible: true,
  };
}

export const resendEmailsConfirmationConfig = (): IConfirmationModalConfig => {
  return {
    title: `Resend Email?`,
    message: `Are you sure you want to resend email to all non-opener users?`,
    icon: {
      show: true,
      color: 'warn',
      name: 'heroicons_outline:exclamation',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Yes',
        color: 'primary',
      },
      cancel: {
        show: true,
        label: 'No',
      },
    },
    dismissible: true,
  }
};

export const pauseSendoutConfirmationConfig = (): IConfirmationModalConfig => ({
  title: 'Pause sending this post?',
  message: 'No more emails go out until you resume it. When resumed, sending continues at the same pace.',
  icon: {
    show: true,
    name: 'heroicons_outline:exclamation',
    color: 'warn',
  },
  actions: {
    confirm: {
      show: true,
      label: 'Pause',
      color: 'primary',
    },
    cancel: {
      show: true,
      label: 'Keep sending',
    },
  },
  dismissible: true,
});

/**
 * With a resend to non-openers scheduled, offers to cancel it too, ticked; the dialog's `primaryActionValue` carries
 * the answer. Without one there is nothing to ask.
 */
export const cancelSendoutConfirmationConfig = (pendingNonOpenerResendAt?: string | null): IConfirmationModalConfig => ({
  title: 'Cancel this send-out?',
  message: 'No more emails go out for this post. Emails already sent stay sent, and a cancelled send-out cannot be resumed.',
  icon: {
    show: true,
    name: 'heroicons_outline:exclamation',
    color: 'warn',
  },
  actions: {
    confirm: {
      show: true,
      label: 'Cancel send-out',
      color: 'warn',
    },
    cancel: {
      show: true,
      label: 'Keep sending',
    },
  },
  dismissible: true,
  ...(pendingNonOpenerResendAt && {
    primaryActionValue: true,
    template: {
      templateType: 'checkbox' as const,
      title: `Also cancel the resend to non-openers scheduled for ${moment(pendingNonOpenerResendAt).format(DATE_FORMAT_WITHOUT_SECONDS)}`,
    },
  }),
});

export const schedulePostSendoutConfirmationConfig = (): IConfirmationModalConfig => {
  return {
    title: `Send-out Schedule Conflict`,
    message: `
      <div>The current date and time do not align with the specified send-out schedule for this post.</div>
      <br />
      <div>Do you wish to procced sending now or schedule for a later date and time?</div>
    `,
    icon: {
      show: true,
      name: 'heroicons_outline:exclamation',
      color: 'info',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Send now',
        color: 'primary',
      },
      cancel: {
        show: true,
        label: 'Cancel',
      },
    },
    dismissible: true,
  }
};

export const deleteScoreboardConfirmationConfig = (details: IScoreboardDetailsConfig): IConfirmationModalConfig => {
  return {
    title: `Delete scoreboard?`,
    message: `Do you want to delete <b>${details.title}</b> scoreboard?`,
    icon: {
      show: true,
      color: 'warn',
      name: 'heroicons_outline:exclamation',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Yes',
        color: 'primary',
      },
      cancel: {
        show: true,
        label: 'No',
      },
    },
    dismissible: true,
  }
};

export const deleteScorecardConfirmationConfig = (details: { title: string }): IConfirmationModalConfig => {
  return {
    title: `Delete scoreboard?`,
    message: ` Deleting a <b>${details.title}</b> will permanently remove it from system.`,
    icon: {
      show: true,
      color: 'warn',
      name: 'heroicons_outline:exclamation',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Yes, Delete Scoreboard',
        color: 'primary',
      },
      cancel: {
        show: true,
        label: 'No, Keep Scoreboard',
      },
    },
    dismissible: true,
  }
};

export const deleteTagConfirmationConfig = (data: ITag): IConfirmationModalConfig => {
  let message: string = `
    <p>
      Deleting a <span class="bold">${data.tagName}</span> will permanently remove it from system.
    </p>
  `;

  return {
    title: `Delete Tag`,
    message: message,
    icon: {
      show: true,
      name: 'heroicons_outline:exclamation',
      color: 'warn',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Delete',
        color: 'warn',
      },
      cancel: {
        show: true,
        label: 'Cancel',
      },
    },
    dismissible: true,
  };
}

export const deleteSmartFlowConfirmationConfig = (data: ISmartFlow): IConfirmationModalConfig => {
  let message: string = `
    <p>
      Deleting a <span class="bold">${data.name}</span> will permanently remove it from system.
    </p>
  `;

  return {
    title: `Delete AutoFlow`,
    message: message,
    icon: {
      show: true,
      name: 'heroicons_outline:exclamation',
      color: 'warn',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Delete',
        color: 'warn',
      },
      cancel: {
        show: true,
        label: 'Cancel',
      },
    },
    dismissible: true,
  };
}

export interface IDeletePostsNoun {
  title: string;
  singular: string;
  plural: string;
}

export const deletePostsConfig = (count: number, noun: IDeletePostsNoun): IConfirmationModalConfig => ({
  title: noun.title,
  message: `Are you sure you want to delete ${count} ${count === 1 ? noun.singular : noun.plural}?`,
  icon: {
    show: true,
    name: 'heroicons_outline:exclamation',
    color: 'warn',
  },
  actions: {
    confirm: {
      show: true,
      label: 'Delete',
      color: 'warn',
    },
    cancel: {
      show: true,
      label: 'Cancel',
    },
  },
  dismissible: true,
});

// Apex DeleteMultiplePostsRequest::MAX_IDS caps POST /api/posts/delete-multiple at 200 ids and answers 422 above it.
export const MAX_BULK_DELETE_IDS: number = 200;

export const bulkDeleteLimitWarning = (count: number): string =>
  `You can delete at most ${MAX_BULK_DELETE_IDS} posts at a time. You have ${count} selected.`;

export const usersBulkDeleteConfirmationConfig = (response: IVerifyEmail): IConfirmationModalConfig => ({
  title: 'Delete Users',
  message: `Are you sure you want to delete ${response.deleteCount} users permanently? <span class="font-medium">This action cannot be undone!</span>`,
  icon: {
    show: true,
    name: 'heroicons_outline:exclamation',
    color: 'warn',
  },
  actions: {
    confirm: {
      show: true,
      label: 'Delete',
      color: 'warn',
    },
    cancel: {
      show: true,
      label: 'Cancel',
    },
  },
  dismissible: true,
  template: {
    templateType: 'confirmInput',
    value: '' + response.deleteCount,
  }
});

export const userImportV2RemoveUsersConfirmation = (response: any): IConfirmationModalConfig => ({
  title: `Remove existing users?`,
  message: `Do you want to Remove existing users who are not in your import file?`,
  icon: {
    show: true,
    color: 'warn',
    name: 'heroicons_outline:exclamation',
  },
  actions: {
    confirm: {
      show: true,
      label: 'Yes',
      color: 'primary',
    },
    cancel: {
      show: true,
      label: 'No',
    },
  },
  dismissible: true,
  template: {
    templateType: 'confirmInput',
    value: 'DELETE ME',
  }
});

export const acceptOptInUserConfirmationConfig = (multiple: boolean = false, isSendWelcomeEmailEnabled: boolean): IConfirmationModalConfig => {
  return {
    title: `Forcefully Accept Opt-In User`,
    message: `
      <div>
        You are going to forcefully add ${multiple ? 'the selected users' : 'this user'} into your system.&nbsp
        ${multiple ? 'The selected users have' : 'This user has'} already received the Opt-in email.
      </div>
      <br/>
      <div>Do you still wish to procced with forcefully adding the ${multiple ? 'selected users' : 'current user'}?</div>
    `,
    icon: {
      show: true,
      name: 'heroicons_outline:exclamation',
      color: 'warning',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Confirm',
        color: 'primary',
      },
      cancel: {
        show: true,
        label: 'Cancel',
      },
    },
    dismissible: true,
    ...(isSendWelcomeEmailEnabled && {
      template: {
        title: 'Send Welcome email',
        templateType: 'slideToggle',
      }
    }),
  }
};

export const welcomeEmailConfirmationConfig = (multiple: boolean = false): IConfirmationModalConfig => {
  return {
    title: `Send Welcome Email`,
    message: `
      <div>${multiple ? 'The selected users have' : 'This user has'} already recevied the the Welcome email.</div>
      <div>Do you still wish to procced sending the Welcome email again.</div>
    `,
    icon: {
      show: true,
      name: 'heroicons_outline:exclamation',
      color: 'warning',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Confirm',
        color: 'primary',
      },
      cancel: {
        show: true,
        label: 'Cancel',
      },
    },
    dismissible: true,
  };
}

export const optInEmailConfirmationConfig = (multiple: boolean = false): IConfirmationModalConfig => {
  return {
    title: `Send Opt-In Email`,
    message: `
      <div>${multiple ? 'The selected users have' : 'This user has'} already recevied the the Opt-in email.</div>
      <div>Do you still wish to procced sending the Opt-in email again.</div>
    `,
    icon: {
      show: true,
      name: 'heroicons_outline:exclamation',
      color: 'warning',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Confirm',
        color: 'primary',
      },
      cancel: {
        show: true,
        label: 'Cancel',
      },
    },
    dismissible: true,
  };
}

export const sendActivationConfirmationConfig = (): IConfirmationModalConfig => {
  return {
    title: `Send activation mail`,
    message: `
      You have successfully added a new admin. Would you like to send an activation email now or defer it to later time?
    `,
    icon: {
      show: false,
      name: 'heroicons_outline:exclamation',
      color: 'info',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Send Activation Email Now',
        color: 'primary',
      },
      cancel: {
        show: true,
        label: 'Send Later',
      },
    },
    dismissible: true,
  }
};

export const becomeSociuuHubUserConfirmationConfig = (user: IUser): IConfirmationModalConfig => {
  return {
    title: `Login as ${user.first_name} to MyHub`,
    message: `Please be aware that you will be accessing ${user.first_name} ${user.last_name}'s MyHub account.<br />
      All actions will be reflected on ${user.first_name}'s MyHub account.<br /><br />
      Your access will last 30 minutes where you need to login again.<br />
      <span class="font-medium">By confirming this dialog you agree on the terms</span>`,
    icon: {
      show: true,
      name: 'heroicons_outline:exclamation',
      color: 'warning',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Login',
        color: 'primary',
      },
      cancel: {
        show: true,
        label: 'Cancel',
      },
    },
    dismissible: false,
  }
};

export const lowCreditBalanceConfirmationConfig = (message: string): IConfirmationModalConfig => {
  return {
    title: 'Low credit balance',
    message: message,
    icon: {
      show: true,
      name: 'heroicons_outline:exclamation',
      color: 'info',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Save as draft',
        color: 'primary',
      },
      cancel: {
        show: true,
        label: 'Close',
      },
    },
    dismissible: true,
  }
}

export const deleteMediaFileConfiramtionConfig = (): IConfirmationModalConfig => {
  return {
    title: 'Remove file',
    message: 'Are you sure you want to remove this permanently? <span class="font-medium">This action cannot be undone!</span>',
    icon: {
      show: true,
      name: 'heroicons_outline:exclamation',
      color: 'warn',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Remove',
        color: 'warn',
      },
      cancel: {
        show: true,
        label: 'Cancel',
      },
    },
    dismissible: true,
  }
}

export const showComplianceContentConfig = (compliance: ICompliance): IConfirmationModalConfig => {
  return {
    title: toParagraphCase(compliance.compliance.title),
    message: compliance.compliance.contents,
    icon: { show: false },
    actions: {
      confirm: { show: false },
      cancel: {
        show: true,
        label: 'Close',
      },
    },
    dismissible: true,
  }
}

export const signupUserConfirmationConfig = (message: string): IConfirmationModalConfig => {
  return {
    title: 'Signup successful',
    message: message || `Welcome aboard! You're now officially part of our community. Get ready for exciting updates and offers.`,
    icon: {
      show: true,
      name: 'feather:user-check',
      color: 'success',
    },
    actions: {
      confirm: { show: false },
      cancel: {
        show: true,
        label: 'Close',
      },
    },
    dismissible: true,
  }
}

export const publishSocialConnectContentConfirmationConfig = (url: string): IConfirmationModalConfig => {
  return {
    title: !url ? `Successfully published!` : `Successfully Posted!`,
    message: !url ? `
      <p>
        Hang tight for just a few more seconds while we upload the Media file. 
        You will receive an email, as soon as it is live on LinkedIn.
      </p>` : `
      <div>
        Your post has been published
      </div>
      ${url && `
        <a href="${url}" target="_blank" class="text-blue-600 underline">
          See post
        </a>
      `}
    `,
    icon: {
      show: true,
      name: 'mat_solid:celebration',
      color: 'success',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Close',
        color: 'primary',
      },
      cancel: {
        show: false,
        label: 'Cancel',
      },
    },
    dismissible: true,
  }
};

export const scheduleSocialConnectContentConfirmationConfig = (): IConfirmationModalConfig => {
  return {
    title: `Successfully scheduled`,
    primaryActionValue: true,
    secondaryActionValue: true,
    template: {
      templateType: 'checkbox',
      title: 'Notifying 24 hour before',
      secondaryAction: {
        templateType: 'checkbox',
        title: 'Send notification mail after it has been published',
      },
    },
    message: `
      <p>
        Your share has been scheduled and will be published automatically at the selected time.
        You’ll receive a confirmation when it goes live.
      </p>`,
    icon: {
      show: true,
      name: 'mat_solid:celebration',
      color: 'success',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Submit',
        color: 'primary',
      },
      cancel: {
        show: false,
        label: 'Cancel',
      },
    },
    dismissible: false,
  }
};

export const disconnectSocialConnectionInCaseOfScheduledContentsConfirmationConfig = (count: number): IConfirmationModalConfig => {
  return {
    title: `Are you sure you want to disconnect?`,
    message: `
      <p>You have ${count} pending scheduled share(s). Disconnecting will prevent them from being published. Click 'Force disconnect' button to disconnect anyway</p>`,
    icon: {
      show: true,
      name: 'heroicons_outline:exclamation',
      color: 'warn',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Force Disconnect',
        color: 'warn',
      },
      cancel: {
        show: true,
        label: 'Cancel',
      },
    },
    dismissible: true,
  }
};

export const publishNativeContentConfirmationConfig = (type: 'create' | 'update' = 'create'): IConfirmationModalConfig => {
  const title: string = type === 'create' ? `Content Created and Ready for Sharing` : `Content Updated and Ready for Sharing`;
  return {
    title: title,
    message: `
      <div>Your ${type === 'create' ? 'new content' : 'content has been updated and'} is now live in MyHub and ready for employees to share.</div>
      <div>Boost engagement by promoting to your colleagues.</div>
    `,
    icon: {
      show: true,
      name: 'mat_solid:celebration',
      color: 'success',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Share to your colleagues',
        color: 'primary',
      },
      cancel: {
        show: true,
        label: 'See Content',
      },
    },
    dismissible: false,
  }
};

export const contentHubRefreshConfirmationConfig = (): IConfirmationModalConfig => {
  return {
    title: 'We are refreshing your Content Hub.',
    message: 'Refresh runs in the background. Sources with an ongoing source update will refresh after that update finishes. Other sources refresh normally. You can close this dialog and continue working.',
    icon: {
      show: true,
      name: 'mat_solid:refresh',
      color: 'info',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Close',
        color: 'accent',
      },
      cancel: {
        show: false,
        label: 'Cancel',
      },
    },
    dismissible: true,
  }
};

export const updateNativeConentConfirmationDialogConfig: FuseConfirmationConfig = {
  title: `Confirm Changes`,
  message: `
    Editing this content may affect future shares by employees and may cause discrepancies with previously sent contnent messages and previews.
    </br>
    Please ensure any changes align with your intended messaging strategy.
    </br>
    </br>
    By confirming, you acknowledge that you understand and accept the potential impacts of these changes.
  `,
  icon: {
    show: true,
    color: 'warning',
    name: 'heroicons_outline:exclamation',
  },
  actions: {
    confirm: {
      show: true,
      label: 'Confirm',
      color: 'primary',
    },
    cancel: {
      show: true,
      label: 'Cancel',
    },
  },
  dismissible: true,
}

export const resetToDefaultUTMTagsConfirmationDialogConfig: FuseConfirmationConfig = {
  title: `Reset All Tracking Parameters`,
  message: `
    This action will completely reset all tracking parameters to their default values. Any changes you’ve made, including renaming, removing, or
    modifying UTM parameters, as well as any custom tracking parameters you’ve added, will be removed. <br /><br />
    Are you sure you want to reset to the default UTM tracking parameters?
  `,
  icon: {
    show: true,
    color: 'error',
    name: 'heroicons_outline:exclamation',
  },
  actions: {
    confirm: {
      show: true,
      label: 'Yes, Reset',
      color: 'primary',
    },
    cancel: {
      show: true,
      label: 'Cancel',
    },
  },
  dismissible: true,
}

export const utmTagParameterRemovalConfirmationDialogConfig: FuseConfirmationConfig = {
  title: `Confirm Parameter Removal`,
  message: `
    You are about to remove this Link Tracking Parameter. Once removed, it cannot be undone. <br /><br />
    Are you sure you want to delete this parameters?
  `,
  icon: {
    show: true,
    color: 'error',
    name: 'heroicons_outline:exclamation',
  },
  actions: {
    confirm: {
      show: true,
      label: 'Yes, remove',
      color: 'warn',
    },
    cancel: {
      show: true,
      label: 'Go Back',
    },
  },
  dismissible: true,
}

export const incompleteUTMParameterConfirmationDialogConfig = (link: string): IConfirmationModalConfig => {
  return {
    title: `Incomplete UTM Parameters Detected`,
    message: `
      We’ve detected that some required UTM parameters are missing, which could affect how your analytics tools
      process campaign data. Without these parameters, your tracking may not function as expected in certain tools.<br /><br />
      <a href="https://www.sociuu.how/utm-tracking" target="_blank">Learn more</a> about UTM best practices and guidelines.<br /><br />
      Would you like to save your changes anyway?
    `,
    icon: {
      show: true,
      color: 'error',
      name: 'heroicons_outline:exclamation',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Yes, save',
        color: 'primary',
      },
      cancel: {
        show: true,
        label: 'Go Back',
      },
    },
    dismissible: true,
  };
}

export const connectToLinkedInConfirmationConfig = (isExpired: boolean = false): IConfirmationModalConfig => {
  return {
    title: isExpired ? `Reconnect LinkedIn to Share Content` : `Connect to LinkedIn to Share Content`,
    message: isExpired
      ? `
        <div>Your LinkedIn connection has expired, which is required to share this post.
        Please reconnect LinkedIn to start sharing your content seamlessly.</div>
      `
      : `
        <div>It looks like you are not connected to LinkedIn, which is required to share this post.
        Please connect to LinkedIn to start sharing your content seamlessly.</div>
      `,
    icon: {
      show: true,
      name: 'heroicons_outline:exclamation',
      color: 'warning',
    },
    actions: {
      confirm: {
        show: true,
        label: isExpired ? 'Reconnect LinkedIn to Share Content' : 'Connect to LinkedIn to Share Content',
        color: 'primary',
      },
      cancel: {
        show: true,
        label: 'Close',
      },
    },
    dismissible: false,
  }
};

/**
 * Example configuration showing how to use the onConfirm callback
 * @param callback - Function to execute when user clicks confirm
 */
export const exampleCallbackConfirmationConfig = (callback: () => void): IConfirmationModalConfig => {
  return {
    title: 'Execute Action',
    message: 'This will execute the provided callback function when you click confirm.',
    icon: {
      show: true,
      name: 'heroicons_outline:information-circle',
      color: 'info',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Execute',
        color: 'primary',
      },
      cancel: {
        show: true,
        label: 'Cancel',
      },
    },
    dismissible: true,
    onConfirm: callback, // This callback will be executed when confirm is clicked
  }
};

export const socialConnectOnSelfSignupConfirmationConfig = (isOptional: boolean = false, onConfirm: () => any): IConfirmationModalConfig => {
  return {
    title: `Welcome aboard!`,
    onConfirm: onConfirm,
    message: `
      ${isOptional ? '<p>You are ready to go.</p>' : '<p>You are almost ready to go.</p>'}
      <div>Want to make the most out of your account?</div>
      <div>Connect your LinkedIn profile ${isOptional ? '(optional)' : '(required)'} and unlock more personalized features.</div>
    `,
    icon: {
      show: true,
      name: isOptional ? 'mat_solid:celebration' : 'heroicons_outline:exclamation',
      color: isOptional ? 'success' : 'info',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Connect to LinkedIn',
        color: 'primary',
      },
      cancel: {
        show: isOptional,
        label: 'Skip',
      },
    },
    dismissible: isOptional,
  }
};

export const ssoSignupRequiredConfirmationConfig = (signupEnabled: boolean): IConfirmationModalConfig => {
  const supportEmail: string = 'support@sociuu.com';
  let message: string = '';
  if (signupEnabled) {
    message = `
      <p>Sorry, you are not yet registered in Sociuu.</p>
      <p>Please <a href="/signup" class="w-full whitespace-nowrap underline">sign up</a> to be part of the program.</p>
    `;
  } else {
    message = `
      <p>Sorry, you are not yet registered in Sociuu.</p>
      <p>Please <a href="mailto:${supportEmail}" class="whitespace-nowrap underline">Contact Support</a> to request access.</p>
    `;
  }

  return {
    title: `Login failed`,
    message: message,
    icon: {
      show: true,
      name: 'heroicons_outline:exclamation',
      color: 'info',
    },
    actions: {
      confirm: {
        show: signupEnabled,
        label: 'Sign up',
        color: 'primary',
      },
      cancel: {
        show: true,
        label: 'Close',
      },
    },
    dismissible: true,
  }
};
